/**
 * Fixed-timestep accumulator. Decouples the variable-rate render loop (rAF, ~60
 * FPS) from the fixed-rate simulation (20 TPS). Given the real elapsed time it
 * advances the simulation in whole ticks and reports an interpolation `alpha`
 * for smooth rendering between the last two sim states.
 *
 * This is engine-agnostic — it does not call requestAnimationFrame itself; the
 * client owns the render loop and feeds delta time in.
 */
import { MS_PER_TICK } from '@settlers/shared';

/** Upper bound on ticks processed per frame, to avoid a "spiral of death" when
 * the tab was backgrounded and a huge delta arrives. */
const MAX_TICKS_PER_FRAME = 5;

export class FixedTimestep {
  private accumulatorMs = 0;

  constructor(private readonly stepMs: number = MS_PER_TICK) {}

  /**
   * Feed the real frame delta (ms). Invokes `step` once per elapsed tick and
   * returns the render interpolation factor alpha ∈ [0, 1).
   */
  advance(deltaMs: number, step: () => void): number {
    // Clamp pathological deltas (tab switch, breakpoint) to the max window.
    this.accumulatorMs += Math.min(deltaMs, this.stepMs * MAX_TICKS_PER_FRAME);

    let ticks = 0;
    while (this.accumulatorMs >= this.stepMs && ticks < MAX_TICKS_PER_FRAME) {
      step();
      this.accumulatorMs -= this.stepMs;
      ticks++;
    }
    return this.accumulatorMs / this.stepMs;
  }

  reset(): void {
    this.accumulatorMs = 0;
  }
}
